import { useState, useEffect } from './wp';
import { fetchCart, removeCartItem, updateCartItem, formatPrice } from './cart';
import { CloseIcon, TrashIcon } from './icons';

/**
 * Mini cart drawer.
 *
 * @param {Object}   props
 * @param {boolean}  props.open
 * @param {Function} props.onClose
 * @param {Object}   props.cart      Store API cart payload (or null while loading)
 * @param {Function} props.onChange  called with a fresh cart after a mutation
 * @param {Object}   props.config    TSA_CHROME
 */
export default function CartDrawer({ open, onClose, cart, onChange, config }) {
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) {
            setBusy('');
            setError('');
        }
    }, [open]);

    const items = (cart && cart.items) || [];
    const totals = cart && cart.totals;

    /**
     * Run a cart write, and recover from a stale item key.
     *
     * The Store API answers 409 "Cart item no longer exists or is invalid"
     * when the key we are holding is not the cart's current one — totals
     * recalculation can re-key a line, and a drawer left open goes stale.
     * This used to swallow that and re-render the same item, so Remove
     * looked like a dead button: members reported hikes they could not take
     * out of the cart while abandoned-cart reminders kept arriving. Refetch,
     * find the same product's current key, and try once more before giving
     * up — and if it still fails, say so instead of failing silently.
     *
     * @param {Function} run  called with a cart item key, returns a promise
     * @param {Object}   item the cart item being acted on
     */
    function mutate(run, item) {
        setBusy(item.key);
        setError('');
        run(item.key)
            .then(onChange)
            .catch(() =>
                fetchCart(config.storeApi).then((fresh) => {
                    onChange(fresh);
                    const current = (fresh.items || []).find(
                        (candidate) => candidate.key === item.key || candidate.id === item.id
                    );
                    if (!current) {
                        return; // already gone from the cart — nothing to report
                    }
                    if (current.key === item.key) {
                        throw new Error('cart write failed');
                    }
                    return run(current.key).then(onChange);
                })
            )
            .catch(() => setError('Sorry — that didn’t work. Please reload the page and try again.'))
            .finally(() => setBusy(''));
    }

    return (
        <div className={'tsa-cart' + (open ? ' is-open' : '')} aria-hidden={!open}>
            <div className="tsa-cart__scrim" onClick={onClose} />
            <aside className="tsa-cart__panel" role="dialog" aria-modal="true" aria-label="Your cart">
                <header className="tsa-cart__head">
                    <h2>Your cart</h2>
                    <button type="button" onClick={onClose} aria-label="Close cart">
                        <CloseIcon />
                    </button>
                </header>

                {error && (
                    <p className="tsa-cart__error" role="alert">
                        {error}
                    </p>
                )}

                {!cart && <p className="tsa-cart__empty">Loading your cart…</p>}

                {cart && items.length === 0 && (
                    <div className="tsa-cart__empty">
                        <p>Your cart is empty.</p>
                        <a className="tsa-cart__cta" href={config.calendar}>
                            Find your next adventure
                        </a>
                    </div>
                )}

                {items.length > 0 && (
                    <ul className="tsa-cart__items">
                        {items.map((item) => {
                            const image = item.images && item.images.length ? item.images[0].thumbnail : '';
                            const variation = (item.variation || [])
                                .map((entry) => `${entry.attribute}: ${entry.value}`)
                                .join(' · ');

                            return (
                                <li key={item.key} className={'tsa-cart__item' + (busy === item.key ? ' is-busy' : '')}>
                                    {image && <img src={image} alt="" width="64" height="64" loading="lazy" />}
                                    <div className="tsa-cart__itembody">
                                        <a className="tsa-cart__itemname" href={item.permalink}>
                                            {item.name}
                                        </a>
                                        {variation && <p className="tsa-cart__itemmeta">{variation}</p>}
                                        <div className="tsa-cart__qty">
                                            <button
                                                type="button"
                                                onClick={() => mutate((key) => updateCartItem(config.storeApi, key, Math.max(1, item.quantity - 1)), item)}
                                                disabled={item.quantity <= 1 || busy === item.key}
                                                aria-label={`Decrease quantity of ${item.name}`}
                                            >
                                                −
                                            </button>
                                            <span>{item.quantity}</span>
                                            <button
                                                type="button"
                                                onClick={() => mutate((key) => updateCartItem(config.storeApi, key, item.quantity + 1), item)}
                                                disabled={busy === item.key}
                                                aria-label={`Increase quantity of ${item.name}`}
                                            >
                                                +
                                            </button>
                                            {/* Labelled, not a bare glyph: an unlabelled
                                                icon between the - / + steppers read as
                                                decoration and members could not find a
                                                way to take something out of the cart. */}
                                            <button
                                                type="button"
                                                className="tsa-cart__remove"
                                                onClick={() => mutate((key) => removeCartItem(config.storeApi, key), item)}
                                                disabled={busy === item.key}
                                                title={`Remove ${item.name}`}
                                                aria-label={`Remove ${item.name}`}
                                            >
                                                <TrashIcon />
                                                <span className="tsa-cart__removetext">Remove</span>
                                            </button>
                                        </div>
                                    </div>
                                    <span className="tsa-cart__itemprice">
                                        {formatPrice(item.totals && item.totals.line_subtotal, totals)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {items.length > 0 && (
                    <footer className="tsa-cart__foot">
                        <div className="tsa-cart__subtotal">
                            <span>Subtotal</span>
                            <strong>{formatPrice(totals && totals.total_items, totals)}</strong>
                        </div>
                        <p className="tsa-cart__note">Taxes and discounts are calculated at checkout.</p>
                        <a className="tsa-cart__cta" href={config.checkout}>
                            Checkout
                        </a>
                        <a className="tsa-cart__link" href={config.cartUrl}>
                            View cart
                        </a>
                    </footer>
                )}
            </aside>
        </div>
    );
}
